import React from 'react'
import { Zap, Check, ArrowRight, MonitorPlay, Bot, Cpu } from 'lucide-react'
const Hero = ({ onExploreOffice, onBookDemo }) => {
  const valuePills = [
    '10 clientes gestionados por solo 500 \u20AC/mes',
    'Menos de 50 \u20AC/mes por cliente',
    'Ejecuci\xF3n continua y calidad Ultra-Premium 24/7',
  ]
  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-tactical-grid">
      {/* Radial green ambient glow behind hero */}
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[450px] md:w-[900px] md:h-[550px] rounded-full pointer-events-none blur-[140px] opacity-25"
        style={{
          background:
            'radial-gradient(circle, rgba(0,255,157,0.7) 0%, rgba(5,51,46,0.3) 50%, transparent 80%)',
        }}
      />
      <div
        className="absolute top-1/3 left-1/4 w-[300px] h-[300px] rounded-full pointer-events-none blur-[100px] opacity-15"
        style={{ background: 'rgba(0, 230, 118, 0.4)' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge superior */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#05332E]/90 border border-[#00FF9D]/30 shadow-[0_0_20px_rgba(0,255,157,0.15)] mb-8 transition-transform hover:scale-[1.02] cursor-default">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF9D] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FF9D]" />
            </span>
            <Zap className="w-4 h-4 text-[#00FF9D] fill-[#00FF9D]" />
            <span className="text-xs sm:text-sm font-semibold tracking-wide text-[#00FF9D]">
              La infraestructura de IA para agencias con +10 clientes
            </span>
          </div>

          {/* Titular H1 */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-[1.1] mb-6">
            Escala el margen de tu agencia con un equipo completo de{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF9D] via-[#00E676] to-[#A7F3D0] inline-block">
              Agentes de IA
            </span>
          </h1>

          {/* Subtitular */}
          <p className="text-lg sm:text-xl text-[#CBD5E1] font-normal leading-relaxed max-w-3xl mx-auto mb-10">
            Diseñamos, construimos e integramos un departamento entero de IA
            hiper-especializado en marketing. Desde la dirección estratégica
            hasta la ejecución de ads, contenidos y analítica, tus clientes
            reciben un servicio Ultra-Premium mientras tu agencia reduce costes
            operativos.
          </p>

          {/* Pills de Valor / Checks */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mb-10">
            {valuePills.map((pill, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#05332E]/80 border border-[#00FF9D]/20 text-xs sm:text-sm font-medium text-white shadow-sm"
              >
                <div className="w-5 h-5 rounded-full bg-[#00FF9D]/20 flex items-center justify-center text-[#00FF9D]">
                  <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                </div>
                <span>{pill}</span>
              </div>
            ))}
          </div>

          {/* Botones de Acción (CTAs) */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <button
              onClick={onBookDemo}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-[#00FF9D] hover:bg-[#00E676] text-[#012622] font-bold text-base transition-all duration-200 shadow-[0_0_30px_rgba(0,255,157,0.4)] hover:shadow-[0_0_40px_rgba(0,255,157,0.6)] hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
            >
              <span>Agendar una Demo (15 min)</span>
              <ArrowRight className="w-5 h-5" />
            </button>

            <button
              onClick={onExploreOffice}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-full bg-[#05332E]/90 hover:bg-[#0A3F39] text-white font-semibold text-base border border-[#00FF9D]/40 hover:border-[#00FF9D] transition-all duration-200 shadow-[0_0_20px_rgba(0,255,157,0.1)] hover:shadow-[0_0_25px_rgba(0,255,157,0.25)] cursor-pointer"
            >
              <MonitorPlay className="w-5 h-5 text-[#00FF9D]" />
              <span>Explorar la Oficina Virtual</span>
            </button>
          </div>
        </div>

        {/* Dashboard Preview Module (Linear/Supabase deep tech aesthetic) */}
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-2xl p-1 bg-gradient-to-b from-[#00FF9D]/40 via-[#05332E]/60 to-[#012622] shadow-[0_20px_60px_rgba(0,0,0,0.8)]">
            {/* Top Mac/Terminal styled bar */}
            <div className="rounded-2xl bg-[#031E1B] border border-[#00FF9D]/20 overflow-hidden">
              <div className="px-4 py-3 bg-[#05332E]/90 border-b border-[#00FF9D]/15 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F56]/80" />
                  <div className="w-3 h-3 rounded-full bg-[#FFBD2E]/80" />
                  <div className="w-3 h-3 rounded-full bg-[#27C93F]/80" />
                  <span className="ml-3 text-xs font-mono text-[#CBD5E1]/70 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#00FF9D] animate-pulse" />
                    hutrit-os // agencia-control-center: connected
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs font-mono text-[#00FF9D]">
                  <span className="px-2.5 py-0.5 rounded bg-[#012622] border border-[#00FF9D]/30">
                    10 Clientes Activos
                  </span>
                  <span className="hidden sm:inline px-2.5 py-0.5 rounded bg-[#012622] border border-[#00FF9D]/30 text-[#CBD5E1]">
                    Coste: 500 €/mes
                  </span>
                </div>
              </div>

              {/* Inner Dashboard View inspired by the uploaded interface screenshot */}
              <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 bg-[#021A17]/95">
                {/* Left: Agent Squad Sidebar */}
                <div className="lg:col-span-4 bg-[#05332E]/60 rounded-xl p-3 border border-[#00FF9D]/15">
                  <div className="flex items-center justify-between mb-3 px-2">
                    <span className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                      <Bot className="w-3.5 h-3.5 text-[#00FF9D]" />
                      Agentes Conectados (9)
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/20">
                      Live
                    </span>
                  </div>

                  <div className="space-y-1.5 text-xs font-mono">
                    {[
                      {
                        name: 'Director de Mkt IA',
                        role: 'Estrategia general',
                        active: true,
                        tag: 'Leader',
                      },
                      {
                        name: 'Alexa (Paid Media)',
                        role: 'Meta & Google Ads',
                        active: true,
                        tag: 'Optimizing',
                      },
                      {
                        name: 'Valentina (Copy)',
                        role: 'Copywriting directo',
                        active: true,
                        tag: 'Writing',
                      },
                      {
                        name: 'Isaura (Dise\xF1o)',
                        role: 'Creatividades visuales',
                        active: true,
                        tag: 'Rendering',
                      },
                      {
                        name: 'Geralis (Analista)',
                        role: 'Reportes & M\xE9tricas',
                        active: true,
                        tag: 'Auditing',
                      },
                      {
                        name: 'Luis & Miguel',
                        role: 'V\xEDdeo corto & Guiones',
                        active: false,
                        tag: 'Standby',
                      },
                    ].map((agent, i) => (
                      <div
                        key={i}
                        className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
                          i === 0
                            ? 'bg-[#0A3F39] border border-[#00FF9D]/30 text-white'
                            : 'hover:bg-[#0A3F39]/40 text-[#CBD5E1]'
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              agent.active
                                ? 'bg-[#00FF9D] animate-ping'
                                : 'bg-gray-500'
                            }`}
                          />
                          <span className="font-medium text-white truncate">
                            {agent.name}
                          </span>
                        </div>
                        <span className="text-[10px] text-[#00FF9D]/90 bg-[#012622] px-1.5 py-0.5 rounded border border-[#00FF9D]/20">
                          {agent.tag}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Center & Right: Live Multi-Agent Workflow Channel */}
                <div className="lg:col-span-8 space-y-4">
                  {/* Real-time agent collaboration feed */}
                  <div className="bg-[#05332E]/40 rounded-xl p-4 border border-[#00FF9D]/15 h-full flex flex-col justify-between">
                    <div className="flex items-center justify-between border-b border-[#0A3F39] pb-3 mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-[#00FF9D]/20 border border-[#00FF9D]/40 flex items-center justify-center text-[#00FF9D]">
                          <Cpu className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-white">
                            Canal de Ejecución Autónoma: Cliente #04 (E-commerce
                            Retail)
                          </div>
                          <div className="text-[10px] text-[#CBD5E1]/70">
                            Flujo: Auditoría de Anuncios → Copywriting → Nuevo
                            Creativo → Ajuste de Puja
                          </div>
                        </div>
                      </div>
                      <span className="text-[11px] font-mono text-[#00FF9D] flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00FF9D] animate-pulse" />
                        0 delay
                      </span>
                    </div>

                    {/* Chat stream dialogue between agents */}
                    <div className="space-y-2.5 text-xs">
                      {/* Alexa Message */}
                      <div className="p-2.5 rounded-lg bg-[#0A3F39]/60 border border-[#00FF9D]/20">
                        <div className="flex items-center justify-between text-[11px] font-mono text-[#00FF9D] mb-1">
                          <span className="font-bold flex items-center gap-1">
                            <span>🤖</span> Alexa (Paid Media):
                          </span>
                          <span className="text-[#CBD5E1]/60">hace 4 min</span>
                        </div>
                        <p className="text-[#CBD5E1]">
                          «Detectada fatiga creativa en conjunto de anuncios
                          &quot;Retargeting Cart&quot;. El CPA subió a 18,20 €. Solicito a{' '}
                          <strong className="text-white">Valentina</strong> 3
                          nuevos ganchos de urgencia y a{' '}
                          <strong className="text-white">Isaura</strong> 2
                          variaciones de formato historia con oferta flash.»
                        </p>
                      </div>

                      {/* Valentina Message */}
                      <div className="p-2.5 rounded-lg bg-[#031E1B] border border-[#00FF9D]/15 ml-3">
                        <div className="flex items-center justify-between text-[11px] font-mono text-[#00FF9D] mb-1">
                          <span className="font-bold flex items-center gap-1">
                            <span>✍️</span> Valentina (Copywriter IA):
                          </span>
                          <span className="text-[#CBD5E1]/60">hace 2 min</span>
                        </div>
                        <p className="text-[#CBD5E1]">
                          «Listos los 3 copies testeados bajo framework PAS.
                          Ángulo 1 enfocado en escasez de stock. Transferidos a
                          Isaura para maquetación.»
                        </p>
                      </div>

                      {/* Director approval */}
                      <div className="p-2.5 rounded-lg bg-[#05332E]/90 border border-[#00FF9D]/40 shadow-inner">
                        <div className="flex items-center justify-between text-[11px] font-mono text-[#00FF9D] mb-1">
                          <span className="font-bold flex items-center gap-1">
                            <span>⚡</span> Director de Marketing IA:
                          </span>
                          <span className="text-[#00FF9D]">
                            Aprobado automáticamente
                          </span>
                        </div>
                        <p className="text-white text-xs">
                          «Variaciones validadas según Brand Guidelines del
                          cliente. Campaña actualizada en Meta Ads API.
                          Estimación de ROAS: 4.8x.»
                        </p>
                      </div>
                    </div>

                    {/* Quick Metric footer */}
                    <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-[#0A3F39]">
                      <div className="p-2 rounded bg-[#012622]/80 border border-[#00FF9D]/15 text-center">
                        <div className="text-[10px] text-[#CBD5E1]/70">
                          Tiempo de Entrega
                        </div>
                        <div className="text-sm font-bold text-[#00FF9D]">
                          14 minutos
                        </div>
                      </div>
                      <div className="p-2 rounded bg-[#012622]/80 border border-[#00FF9D]/15 text-center">
                        <div className="text-[10px] text-[#CBD5E1]/70">
                          Coste Operativo
                        </div>
                        <div className="text-sm font-bold text-white">
                          &lt; 1,70 € / día
                        </div>
                      </div>
                      <div className="p-2 rounded bg-[#012622]/80 border border-[#00FF9D]/15 text-center">
                        <div className="text-[10px] text-[#CBD5E1]/70">
                          Margen Agencia
                        </div>
                        <div className="text-sm font-bold text-[#00FF9D]">
                          +86%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
export { Hero }
